import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetInventoryMovements } from "../../../hooks/inventory/useGetInventoryMovements";
import { useGetWarehouses } from "../../../hooks/warehouses/useGetWarehouses";
import useTableActions from "../../../hooks/common/useTableActions";

import TableLayout from "../../global/Table/TableLayout";
import { MovementsTableColumns } from "./Table/MovementsTableColumns";
import MovementsTableRow from "./Table/MovementsTableRow";

const REASON_OPTIONS = [
    { value: "",                  label: { ar: "كل الأسباب",      en: "All reasons" } },
    { value: "purchase",          label: { ar: "شراء",            en: "Purchase" } },
    { value: "sale",              label: { ar: "بيع",             en: "Sale" } },
    { value: "return",            label: { ar: "مرتجع",           en: "Return" } },
    { value: "adjustment_add",    label: { ar: "تعديل (إضافة)",   en: "Adjustment (Add)" } },
    { value: "adjustment_remove", label: { ar: "تعديل (إزالة)",   en: "Adjustment (Remove)" } },
    { value: "transfer_in",       label: { ar: "تحويل وارد",      en: "Transfer In" } },
    { value: "transfer_out",      label: { ar: "تحويل صادر",      en: "Transfer Out" } },
    { value: "damaged",           label: { ar: "تالف",            en: "Damaged" } },
    { value: "correction",        label: { ar: "تصحيح",           en: "Correction" } },
    { value: "initial",           label: { ar: "مخزون ابتدائي",   en: "Initial" } },
];

const REFERENCE_OPTIONS = [
    { value: "",         label: { ar: "كل المراجع", en: "All references" } },
    { value: "order",    label: { ar: "طلب",        en: "Order" } },
    { value: "return",   label: { ar: "مرتجع",      en: "Return" } },
    { value: "manual",   label: { ar: "يدوي",       en: "Manual" } },
    { value: "transfer", label: { ar: "تحويل",      en: "Transfer" } },
];

const MovementsTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const [warehouseFilter, setWarehouseFilter] = useState("");
    const [reasonFilter, setReasonFilter] = useState("");
    const [referenceTypeFilter, setReferenceTypeFilter] = useState("");

    const { warehouses } = useGetWarehouses({ per_page: 200 });

    const {
        page, perPage, search, order, selectedIds, menuAnchorEl,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handlePageChange, handlePerPageChange,
    } = useTableActions({ basePath: "/inventory/movements" });

    const params = useMemo(() => ({
        page, per_page: perPage,
        warehouse_id: warehouseFilter || undefined,
        reason: reasonFilter || undefined,
        reference_type: referenceTypeFilter || undefined,
    }), [page, perPage, warehouseFilter, reasonFilter, referenceTypeFilter]);

    const { movements, meta, isLoading, error, refetch } = useGetInventoryMovements(params);

    const warehouseOptions = useMemo(() => ([
        { value: "", label: { ar: "كل المستودعات", en: "All Warehouses" } },
        ...warehouses.map((w) => ({
            value: String(w.id),
            label: { ar: w.name || "-", en: w.name || "-" },
        })),
    ]), [warehouses]);

    const tableFilters = [
        {
            name: "warehouse",
            label: { ar: "المستودع", en: "Warehouse" },
            value: warehouseFilter,
            onChange: setWarehouseFilter,
            options: warehouseOptions,
        },
        {
            name: "reason",
            label: { ar: "السبب", en: "Reason" },
            value: reasonFilter,
            onChange: setReasonFilter,
            options: REASON_OPTIONS,
        },
        {
            name: "reference_type",
            label: { ar: "نوع المرجع", en: "Reference Type" },
            value: referenceTypeFilter,
            onChange: setReferenceTypeFilter,
            options: REFERENCE_OPTIONS,
        },
    ];

    const renderRow = (rowParams) => (
        <MovementsTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
        />
    );

    return (
        <TableLayout
            title={isArabic ? "حركات المخزون" : "Stock Movements"}
            subtitle={isArabic ? "سجل تدقيقي لكل تغيير في المخزون" : "Audit log of every stock change"}
            search={search}
            onSearchChange={setSearch}
            addButtonVisible={false}
            isLoading={isLoading}
            error={error}
            items={movements}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={MovementsTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            menuDisableEdit
            menuDisableDelete
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, movements)}
            onRowCheckboxClick={handleRowCheckboxClick}
            bulkDeleteDisabled
            filters={tableFilters}
            onClearAllFilters={() => {
                setWarehouseFilter("");
                setReasonFilter("");
                setReferenceTypeFilter("");
            }}
            emptyLabelAr="لا توجد حركات مخزون"
            emptyLabelEn="No stock movements"
            refetch={refetch}
        />
    );
};

export default MovementsTable;
